<template>
    <div class="vf-selfie">
        <!--
          Carries the element's own fieldId: vueform focuses `#<fieldId>` after a
          remove (usePreview) with no null check, and the element's <label for> points
          here too. Without the id, removing a photo throws.
        -->
        <input
            v-show="false"
            type="file"
            :id="fieldId"
            :accept="accept || 'image/*'"
            @change="handlePick"
            ref="input"
        />

        <div v-if="!src && !cameraActive" class="vf-selfie-actions">
            <button type="button" class="uk-button uk-button-default uk-button-small" @click.prevent="pickFile">
                <span uk-icon="icon: upload; ratio: 0.85" class="uk-margin-small-right"></span>{{ t.select }}
            </button>
            <!--
              While the browser's permission prompt is open getUserMedia stays pending,
              so the label carries the waiting state — a merely disabled button reads as
              broken.
            -->
            <button
                v-if="cameraEnabled"
                type="button"
                class="uk-button uk-button-default uk-button-small"
                :disabled="cameraStarting"
                @click.prevent="startCamera()"
            >
                <span uk-icon="icon: camera; ratio: 0.85" class="uk-margin-small-right"></span>{{ cameraStarting ? t.camera_starting : t.camera }}
            </button>
            <span class="uk-text-meta">{{ ratioHint }}</span>
        </div>

        <!--
          Live preview, cover-cropped to the very frame the crop stage uses, so the
          section captured is the section shown. Deliberately not mirrored: the subject
          stands in front of the camera, and a mirrored pass photo prints reversed.
        -->
        <div v-if="cameraActive" class="vf-selfie-stage-wrap">
            <video ref="video" class="vf-selfie-video" :style="frameStyle" playsinline autoplay muted></video>

            <div class="vf-selfie-actions">
                <button type="button" class="uk-button uk-button-primary uk-button-small" @click.prevent="capturePhoto">
                    {{ t.capture }}
                </button>
                <button type="button" class="uk-button uk-button-text uk-button-small" @click.prevent="stopCamera">
                    {{ t.cancel }}
                </button>
                <!--
                  v-model, not :value — a bound value is patched before the options exist
                  in the DOM, so the select would render empty while a camera is live.
                -->
                <select
                    v-if="cameraDevices.length > 1"
                    v-model="cameraDeviceId"
                    class="uk-select uk-form-small uk-form-width-medium"
                    @change="switchCamera"
                >
                    <option v-for="(device, index) in cameraDevices" :key="device.deviceId" :value="device.deviceId">
                        {{ device.label || `${t.camera_label} ${index + 1}` }}
                    </option>
                </select>
            </div>
        </div>

        <div v-if="src && !cameraActive" class="vf-selfie-stage-wrap">
            <div
                ref="stage"
                class="vf-selfie-stage"
                :style="frameStyle"
                @pointerdown="startDrag"
                @wheel.prevent="onWheel"
            >
                <img ref="image" :src="src" :style="imageStyle" alt="" @load="onImageLoaded" />
                <div class="vf-selfie-frame"></div>
            </div>

            <div class="vf-selfie-zoom">
                <span class="uk-text-meta">{{ t.zoom }}</span>
                <input type="range" class="uk-range" min="1" max="4" step="0.01" :value="zoom" @input="onZoomInput" />
                <button type="button" class="uk-button uk-button-text uk-button-small" @click.prevent="resetGeometry">
                    {{ t.reset }}
                </button>
            </div>

            <p class="uk-text-meta uk-margin-remove">{{ t.hint }}</p>

            <div class="vf-selfie-actions">
                <button type="button" class="uk-button uk-button-primary uk-button-small" @click.prevent="commitCrop">
                    {{ t.confirm }}
                </button>
                <button type="button" class="uk-button uk-button-text uk-button-small" @click.prevent="discard">
                    {{ t.discard }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * Crop stage for `selfie` fields: the photo is panned and zoomed *behind* a frame
 * locked to `options.aspectRatio` (the project's pass photo format in php-core), so
 * the ratio cannot be violated. Confirming renders the framed area into a canvas and
 * emits it as a `File`.
 *
 * @warn A plain, self-contained component on purpose — NOT a vueform element template.
 *   vueform renders a template override's `<template>` only; its `data`, `computed`
 *   and `methods` are ignored, because the instance stays the element itself (that is
 *   why every other override here keeps its state in `plugins.js`). All the state
 *   below therefore lives in a component vueform does not touch, and
 *   `SelfieElement.vue` just embeds it.
 *
 * @see php-core `Festiware\FestiwareFields\ImageCropper` — same interaction in Nova.
 */
export default {
    name: 'SelfieCropper',

    props: {
        // { aspectRatio, aspectRatioLabel, outputLongEdge, camera } — see
        // VueFormController::selfieOptions() in php-core.
        options: { type: Object, default: () => ({}) },
        // form$.translations.festiware.selfie
        translations: { type: Object, default: () => ({}) },
        accept: { type: String, default: 'image/*' },
        // The element's own input id — see the input above.
        fieldId: { type: String, default: null },
    },

    emits: ['cropped', 'notify'],

    data() {
        return {
            src: null,
            naturalWidth: 0,
            naturalHeight: 0,
            // Top-left corner of the scaled image inside the frame, in frame pixels.
            offsetX: 0,
            offsetY: 0,
            zoom: 1,
            dragStart: null,
            cameraActive: false,
            cameraStarting: false,
            cameraStream: null,
            cameraDevices: [],
            cameraDeviceId: null,
        }
    },

    computed: {
        t() {
            return this.translations || {}
        },

        ratio() {
            const ratio = parseFloat(this.options.aspectRatio)

            return ratio > 0 ? ratio : 1
        },

        // Longer edge of the on-screen frame in CSS pixels.
        frameLongEdge() {
            return 280
        },

        frameWidth() {
            return this.ratio >= 1 ? this.frameLongEdge : Math.round(this.frameLongEdge * this.ratio)
        },

        frameHeight() {
            return this.ratio >= 1 ? Math.round(this.frameLongEdge / this.ratio) : this.frameLongEdge
        },

        frameStyle() {
            return { width: `${this.frameWidth}px`, height: `${this.frameHeight}px` }
        },

        /**
         * Cameras exist only in a secure context; over plain http `mediaDevices` is
         * undefined, and then hiding the button is the honest answer.
         */
        cameraEnabled() {
            return (
                this.options.camera !== false &&
                typeof navigator !== 'undefined' &&
                !!navigator.mediaDevices &&
                typeof navigator.mediaDevices.getUserMedia === 'function'
            )
        },

        // Scale at which the image exactly covers the frame — the zoom-1 baseline.
        baseScale() {
            if (!this.naturalWidth || !this.naturalHeight) return 1

            return Math.max(this.frameWidth / this.naturalWidth, this.frameHeight / this.naturalHeight)
        },

        scale() {
            return this.baseScale * this.zoom
        },

        imageStyle() {
            return {
                width: `${this.naturalWidth * this.scale}px`,
                height: `${this.naturalHeight * this.scale}px`,
                transform: `translate(${this.offsetX}px, ${this.offsetY}px)`,
            }
        },

        ratioHint() {
            const label = this.options.aspectRatioLabel || (this.ratio === 1 ? '1:1' : this.ratio.toFixed(2))

            return `${this.t.ratio ?? ''} ${label}`.trim()
        },
    },

    mounted() {
        window.addEventListener('pointermove', this.onDrag)
        window.addEventListener('pointerup', this.stopDrag)
    },

    beforeUnmount() {
        window.removeEventListener('pointermove', this.onDrag)
        window.removeEventListener('pointerup', this.stopDrag)
        // Leaving the form (tab switch, submit, unmount) must switch the camera light off.
        this.releaseStream()
        this.revokeSource()
    },

    methods: {
        pickFile() {
            this.$refs.input.click()
        },

        handlePick(event) {
            const file = event.target.files && event.target.files[0]
            event.target.value = ''

            if (file) this.replaceSource(URL.createObjectURL(file))
        },

        replaceSource(src) {
            this.revokeSource()
            this.src = src
        },

        revokeSource() {
            if (this.src && this.src.startsWith('blob:')) URL.revokeObjectURL(this.src)
        },

        discard() {
            this.revokeSource()
            this.src = null
        },

        /**
         * Opens a camera and shows a live preview inside the crop frame. Called without
         * an id for the browser's default camera, with one when switching devices.
         */
        async startCamera(deviceId = null) {
            if (!this.cameraEnabled || this.cameraStarting) return false

            this.cameraStarting = true

            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: false,
                    video: deviceId
                        ? { deviceId: { exact: deviceId }, width: { ideal: 1280 }, height: { ideal: 1280 } }
                        : { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 1280 } },
                })

                // Release the previous stream only once the new one exists, otherwise a
                // failed device switch leaves the user with no preview at all.
                this.releaseStream()

                this.cameraStream = stream
                this.cameraDeviceId = deviceId || this.activeDeviceId(stream)

                // Resolve the device list *before* the preview appears: the select is
                // v-model-bound and one that mounts while the model is still null
                // renders with no selection at all.
                await this.loadCameraDevices()

                this.cameraActive = true

                await this.$nextTick()
                this.attachStream()

                return true
            } catch (error) {
                this.reportCameraError(error)

                return false
            } finally {
                this.cameraStarting = false
            }
        },

        attachStream() {
            const video = this.$refs.video
            if (!video) return

            video.srcObject = this.cameraStream
            // Autoplay can still be rejected (e.g. reduced-motion policies); ignoring
            // the rejection keeps the console clean, the user sees a frozen frame.
            const started = video.play()
            if (started && typeof started.catch === 'function') started.catch(() => {})
        },

        activeDeviceId(stream) {
            const track = stream.getVideoTracks()[0]
            if (!track || typeof track.getSettings !== 'function') return null

            return track.getSettings().deviceId || null
        },

        /**
         * Device labels stay empty until camera permission was granted, so this runs
         * after getUserMedia — never before.
         */
        async loadCameraDevices() {
            if (typeof navigator.mediaDevices.enumerateDevices !== 'function') return

            try {
                const devices = await navigator.mediaDevices.enumerateDevices()
                this.cameraDevices = devices.filter((device) => device.kind === 'videoinput')

                // The running track may report no deviceId at all, or one that isn't in
                // the enumerated list. Either way the select would match no option and
                // render empty while a camera is live, so fall back to the first entry.
                const known = this.cameraDevices.some((device) => device.deviceId === this.cameraDeviceId)

                if (!known && this.cameraDevices.length) {
                    this.cameraDeviceId = this.cameraDevices[0].deviceId
                }
            } catch (error) {
                this.cameraDevices = []
            }
        },

        /**
         * v-model has already moved the select to the new device, so a failed switch
         * has to put the label back on the camera that is actually running.
         */
        async switchCamera(event) {
            const previous = this.cameraStream ? this.activeDeviceId(this.cameraStream) : null

            if (!(await this.startCamera(event.target.value))) {
                this.cameraDeviceId = previous || (this.cameraDevices[0] && this.cameraDevices[0].deviceId) || null
            }
        },

        /**
         * Grabs the current frame at the camera's native resolution. The result is a
         * data URL, so unlike a remote source it can never taint the canvas later.
         */
        capturePhoto() {
            const video = this.$refs.video

            if (!video || !video.videoWidth || !video.videoHeight) {
                this.notify(this.t.camera_not_ready)

                return
            }

            const canvas = document.createElement('canvas')
            canvas.width = video.videoWidth
            canvas.height = video.videoHeight
            canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height)

            const photo = canvas.toDataURL('image/jpeg', 0.95)

            this.stopCamera()
            this.replaceSource(photo)
        },

        stopCamera() {
            this.releaseStream()
            this.cameraActive = false
        },

        releaseStream() {
            if (!this.cameraStream) return

            this.cameraStream.getTracks().forEach((track) => track.stop())
            this.cameraStream = null
        },

        reportCameraError(error) {
            const name = error && error.name
            const messages = {
                NotAllowedError: this.t.camera_denied,
                SecurityError: this.t.camera_denied,
                NotFoundError: this.t.camera_missing,
                OverconstrainedError: this.t.camera_missing,
                NotReadableError: this.t.camera_busy,
                AbortError: this.t.camera_busy,
            }

            this.notify(messages[name] || this.t.camera_failed)
        },

        notify(message) {
            if (!message) return

            this.$emit('notify', message)
        },

        onImageLoaded(event) {
            this.naturalWidth = event.target.naturalWidth
            this.naturalHeight = event.target.naturalHeight
            this.resetGeometry()
        },

        resetGeometry() {
            this.zoom = 1
            this.centerImage()
        },

        centerImage() {
            this.offsetX = (this.frameWidth - this.naturalWidth * this.scale) / 2
            this.offsetY = (this.frameHeight - this.naturalHeight * this.scale) / 2
        },

        onZoomInput(event) {
            this.applyZoom(parseFloat(event.target.value))
        },

        onWheel(event) {
            this.applyZoom(this.zoom * (event.deltaY < 0 ? 1.08 : 1 / 1.08))
        },

        /**
         * Zoom around the frame centre, so the visible detail stays put instead of
         * drifting towards the image's top-left corner.
         */
        applyZoom(requested) {
            const next = Math.min(4, Math.max(1, requested || 1))
            const previousScale = this.scale

            this.zoom = next

            const factor = this.scale / previousScale
            this.offsetX = this.frameWidth / 2 - (this.frameWidth / 2 - this.offsetX) * factor
            this.offsetY = this.frameHeight / 2 - (this.frameHeight / 2 - this.offsetY) * factor

            this.clampOffsets()
        },

        startDrag(event) {
            if (!this.src) return

            this.dragStart = { x: event.clientX, y: event.clientY, offsetX: this.offsetX, offsetY: this.offsetY }
        },

        onDrag(event) {
            if (!this.dragStart) return

            this.offsetX = this.dragStart.offsetX + (event.clientX - this.dragStart.x)
            this.offsetY = this.dragStart.offsetY + (event.clientY - this.dragStart.y)
            this.clampOffsets()
        },

        stopDrag() {
            this.dragStart = null
        },

        /** The frame must never show empty space: the image always covers it fully. */
        clampOffsets() {
            const displayedWidth = this.naturalWidth * this.scale
            const displayedHeight = this.naturalHeight * this.scale

            this.offsetX = Math.min(0, Math.max(this.frameWidth - displayedWidth, this.offsetX))
            this.offsetY = Math.min(0, Math.max(this.frameHeight - displayedHeight, this.offsetY))
        },

        /** Hands the framed area to the parent as a plain File. */
        commitCrop() {
            const blob = this.renderBlob()
            if (!blob) return

            this.$emit('cropped', new File([blob], 'selfie.jpg', { type: 'image/jpeg' }))
            this.discard()
        },

        /**
         * Renders the framed area into a canvas and returns it as a JPEG blob. Done
         * synchronously (toDataURL instead of toBlob) so callers don't have to thread a
         * second await through the commit path.
         */
        renderBlob() {
            const image = this.$refs.image
            if (!image || !image.naturalWidth) return null

            const longEdge = parseInt(this.options.outputLongEdge, 10) || 600
            const outputWidth = this.ratio >= 1 ? longEdge : Math.round(longEdge * this.ratio)
            const outputHeight = this.ratio >= 1 ? Math.round(longEdge / this.ratio) : longEdge

            const canvas = document.createElement('canvas')
            canvas.width = outputWidth
            canvas.height = outputHeight

            const context = canvas.getContext('2d')
            context.fillStyle = '#ffffff'
            context.fillRect(0, 0, outputWidth, outputHeight)

            // Frame → natural image coordinates.
            const sourceX = -this.offsetX / this.scale
            const sourceY = -this.offsetY / this.scale
            const sourceWidth = this.frameWidth / this.scale
            const sourceHeight = this.frameHeight / this.scale

            try {
                context.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, outputWidth, outputHeight)

                return this.dataUrlToBlob(canvas.toDataURL('image/jpeg', 0.92))
            } catch (error) {
                // Tainted canvas or a decode failure: submit nothing rather than a blank
                // photo, and say so.
                this.notify(this.t.crop_failed)

                return null
            }
        },

        dataUrlToBlob(dataUrl) {
            const base64 = dataUrl.split(',')[1]
            const binary = atob(base64)
            const bytes = new Uint8Array(binary.length)

            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i)
            }

            return new Blob([bytes], { type: 'image/jpeg' })
        },
    },
}
</script>

<style lang="scss">
/* Styles live in assets/styles/newform/_selfie.scss — the theme compiles that
   directory into the combined CSS the contributor portal actually loads.
   Anything put here only ends up in the parcel bundle. */
</style>
